import LatLngLiteral = google.maps.LatLngLiteral;
import PolygonOptions = google.maps.PolygonOptions;
import DrawingManagerOptions = google.maps.drawing.DrawingManagerOptions;
import {useCallback, useEffect, useState} from "react";
import {DrawingManager, PolygonF} from "@react-google-maps/api";

export interface EditablePolygon {
  paths: LatLngLiteral[];
  instance?: google.maps.Polygon;
  area?: number;
}

export interface EditablePolygonCompleteEvent {
  paths: LatLngLiteral[];
  polygon: google.maps.Polygon;
  area?: number;
}

export interface EditablePolygonDeleteEvent {
  paths: LatLngLiteral[];
  index: number;
}

export type PolygonEditorProps = {
  onPolygonDelete?: (polygon: EditablePolygonDeleteEvent) => void;
  onPolygonComplete?: (data: EditablePolygonCompleteEvent) => void;
  options?: Partial<DrawingManagerOptions>;
  polygonOptions?: Partial<PolygonOptions>;
  drawingMode?: google.maps.drawing.OverlayType;
}

export const PolygonEditor = (props: PolygonEditorProps) => {
  const {
    onPolygonDelete,
    onPolygonComplete,
    options = {},
    polygonOptions = {},
    drawingMode = google.maps.drawing.OverlayType.POLYGON
  } = props

  const [polygons, setPolygons] = useState<EditablePolygon[]>([]);
  const [selectedIdx, setSelectedIdx] = useState<number>(-1);
  const [drawingManager, setDrawingManager] = useState<google.maps.drawing.DrawingManager | null>(null);

  const handlePolygonComplete = useCallback(
    (drawn: google.maps.Polygon) => {
      const path = drawn.getPath();
      const paths = path.getArray().map((ll) => ({lat: ll.lat(), lng: ll.lng()}));

      if (paths.length < 3) {
        drawn.setMap(null);
        return;
      }

      const area = google.maps.geometry?.spherical.computeArea(path) ?? undefined;
      if (area !== undefined && area <= 0) {
        drawn.setMap(null);
        return;
      }

      const newPoly: EditablePolygon = {paths, instance: drawn, area};
      setPolygons((p) => [...p, newPoly]);
      onPolygonComplete?.({paths, polygon: drawn, area});
      setSelectedIdx(polygons.length); // auto-select the one just drawn

      drawn.setMap(null)
      drawingManager.setDrawingMode(null);
    },
    [onPolygonComplete, polygons.length, drawingManager]
  );

  const handlePolygonClick = useCallback(
    (idx: number) => () => {
      setSelectedIdx(idx);
    },
    []
  );

  const deleteSelected = useCallback(() => {
    if (selectedIdx === -1) return;

    const poly = polygons[selectedIdx];
    poly.instance?.setMap(null); // remove from map

    const deletedPaths = poly.paths;
    setPolygons((p) => p.filter((_, i) => i !== selectedIdx));
    setSelectedIdx(-1);

    onPolygonDelete?.({paths: deletedPaths, index: selectedIdx});
  }, [selectedIdx, polygons, onPolygonDelete]);

  // Keyboard support
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        deleteSelected();
      } else if (e.key === 'Escape') {
        drawingManager.setDrawingMode(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [deleteSelected, drawingManager]);

  const baseOptions : google.maps.PolygonOptions = {
    fillColor: '#FF0000',
    fillOpacity: 0.3,
    strokeColor: '#FF0000',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    clickable: true,
    editable: true,
    draggable: true,
    ...polygonOptions,
  };

  const selectedOptions : google.maps.PolygonOptions = {
    ...baseOptions,
    strokeColor: '#22A7F0',
    strokeWeight: 10,
    fillOpacity: 0.5,
  };

  const drawingOptions: DrawingManagerOptions = {
    drawingMode,
    drawingControl: true,
    drawingControlOptions: {
      position: google.maps.ControlPosition.TOP_CENTER,
      drawingModes: [google.maps.drawing.OverlayType.POLYGON],
    },
    polygonOptions: baseOptions,
    ...options,
  };

  return (
    <>
      <DrawingManager
        onLoad={(ref) => setDrawingManager(ref)}
        options={drawingOptions}
        onPolygonComplete={handlePolygonComplete}
      />

      {polygons.map((poly, idx) => (
        <PolygonF
          key={idx}
          paths={poly.paths}
          options={idx === selectedIdx ? selectedOptions : baseOptions}
          onRightClick={e => handlePolygonClick(idx)}
        />
      ))}
    </>
  )
}
